import { useState, useEffect } from 'react';
import { collection, getDocs, doc, updateDoc, addDoc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { SupportMessage } from '../../models/types';
import { MessageCircle, Send, Clock, CheckCircle, User, Mail, Calendar } from 'lucide-react';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';

const AdminSupport = () => {
  const { currentUser, userData } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [supportMessages, setSupportMessages] = useState<SupportMessage[]>([]);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState<string | null>(null);
  const [responses, setResponses] = useState<{ [key: string]: string }>({});
  const [filter, setFilter] = useState<'all' | 'unanswered' | 'answered'>('all');

  useEffect(() => {
    if (userData?.role !== 'admin') {
      navigate('/');
      return;
    }

    fetchSupportMessages();
  }, [userData, navigate]);

  const fetchSupportMessages = async () => {
    try {
      setLoading(true);
      
      const supportRef = collection(db, 'supportMessages');
      const q = query(supportRef, orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      
      const messagesList: SupportMessage[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        messagesList.push({
          id: doc.id,
          ...data,
          createdAt: data.createdAt?.toDate() || new Date(),
          answeredAt: data.answeredAt?.toDate()
        } as SupportMessage);
      });
      
      setSupportMessages(messagesList);
    } catch (error) {
      console.error('Error fetching support messages:', error);
      toast.error('Error loading support messages');
    } finally {
      setLoading(false);
    }
  };

  const handleResponseChange = (messageId: string, response: string) => {
    setResponses(prev => ({
      ...prev,
      [messageId]: response
    }));
  };

  const handleSendResponse = async (message: SupportMessage) => {
    const response = responses[message.id];
    if (!response?.trim() || !currentUser) return;

    try {
      setResponding(message.id);
      
      // Update the support message
      await updateDoc(doc(db, 'supportMessages', message.id), {
        answered: true,
        adminResponse: response.trim(),
        adminId: currentUser.uid,
        answeredAt: serverTimestamp()
      });

      // Create a notification for the user
      const notificationData = {
        userId: message.userId,
        title: 'Support Response Received',
        message: `Our support team has responded to your message: "${message.message.substring(0, 50)}${message.message.length > 50 ? '...' : ''}"`,
        type: 'support',
        read: false,
        createdAt: serverTimestamp(),
        metadata: {
          supportMessageId: message.id
        }
      };

      await addDoc(collection(db, 'notifications'), notificationData);
      
      // Update local state
      setSupportMessages(prev => prev.map(msg => 
        msg.id === message.id 
          ? { 
              ...msg, 
              answered: true, 
              adminResponse: response.trim(),
              adminId: currentUser.uid,
              answeredAt: new Date()
            }
          : msg
      ));

      // Clear the response input
      setResponses(prev => ({
        ...prev,
        [message.id]: ''
      }));

      toast.success('Response sent successfully!');
    } catch (error) {
      console.error('Error sending response:', error);
      toast.error('Error sending response. Please try again.');
    } finally {
      setResponding(null);
    }
  };

  const filteredMessages = supportMessages.filter(message => {
    switch (filter) {
      case 'unanswered':
        return !message.answered;
      case 'answered':
        return message.answered;
      default:
        return true;
    }
  });

  const stats = {
    total: supportMessages.length,
    unanswered: supportMessages.filter(m => !m.answered).length,
    answered: supportMessages.filter(m => m.answered).length
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <h1 className="text-2xl font-bold mb-6 flex items-center">
        <MessageCircle className="h-6 w-6 text-primary-600 mr-2" />
        Support Messages
      </h1>

      {/* Stats Overview */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div 
          className={`bg-white rounded-lg shadow-md p-4 cursor-pointer transition-colors hover:bg-gray-50 ${
            filter === 'all' ? 'ring-2 ring-primary-600' : ''
          }`}
          onClick={() => setFilter('all')}
        >
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Total Messages</h3>
            <MessageCircle className="h-5 w-5 text-gray-500" />
          </div>
          <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
        </div>

        <div 
          className={`bg-white rounded-lg shadow-md p-4 cursor-pointer transition-colors hover:bg-gray-50 ${
            filter === 'unanswered' ? 'ring-2 ring-primary-600' : ''
          }`}
          onClick={() => setFilter('unanswered')}
        >
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Unanswered</h3>
            <Clock className="h-5 w-5 text-yellow-500" />
          </div>
          <p className="text-2xl font-bold text-yellow-600">{stats.unanswered}</p>
        </div>

        <div 
          className={`bg-white rounded-lg shadow-md p-4 cursor-pointer transition-colors hover:bg-gray-50 ${
            filter === 'answered' ? 'ring-2 ring-primary-600' : ''
          }`}
          onClick={() => setFilter('answered')}
        >
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-medium text-gray-500">Answered</h3>
            <CheckCircle className="h-5 w-5 text-green-500" />
          </div>
          <p className="text-2xl font-bold text-green-600">{stats.answered}</p>
        </div>
      </div>

      {/* Messages List */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold">
            {filter === 'all' ? 'All Messages' : filter === 'unanswered' ? 'Unanswered Messages' : 'Answered Messages'}
            ({filteredMessages.length})
          </h2>
        </div>

        {filteredMessages.length === 0 ? (
          <div className="text-center py-8">
            <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">
              {filter === 'unanswered' ? 'No unanswered messages' : 
               filter === 'answered' ? 'No answered messages' : 'No support messages yet'}
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {filteredMessages.map((message) => (
              <div
                key={message.id}
                className={`border rounded-lg p-6 ${
                  message.answered ? 'border-green-200 bg-green-50' : 'border-yellow-200 bg-yellow-50'
                }`}
              >
                {/* Message Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-3">
                    <div className={`p-2 rounded-full ${
                      message.userRole === 'requester' ? 'bg-blue-100' : 'bg-purple-100'
                    }`}>
                      <User className={`h-5 w-5 ${
                        message.userRole === 'requester' ? 'text-blue-600' : 'text-purple-600'
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{message.userName}</h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Mail className="h-4 w-4 mr-1" />
                          {message.userEmail}
                        </div>
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          message.userRole === 'requester' 
                            ? 'bg-blue-100 text-blue-700' 
                            : 'bg-purple-100 text-purple-700'
                        }`}>
                          {message.userRole}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    <div className="flex items-center text-sm text-gray-500">
                      <Calendar className="h-4 w-4 mr-1" />
                      {format(message.createdAt, 'MMM d, yyyy HH:mm')}
                    </div>
                    <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                      message.answered 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {message.answered ? 'Answered' : 'Pending'}
                    </span>
                  </div>
                </div>

                {/* User Message */}
                <div className="mb-4">
                  <h4 className="text-sm font-medium text-gray-700 mb-2">User Message:</h4>
                  <div className="bg-white p-4 rounded-lg border">
                    <p className="text-gray-800 whitespace-pre-wrap">{message.message}</p>
                  </div>
                </div>

                {/* Admin Response Section */}
                {message.answered ? (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Admin Response:</h4>
                    <div className="bg-primary-50 p-4 rounded-lg border border-primary-200">
                      <p className="text-gray-800 whitespace-pre-wrap">{message.adminResponse}</p>
                      {message.answeredAt && (
                        <p className="text-sm text-gray-500 mt-2">
                          Responded on {format(message.answeredAt, 'MMM d, yyyy HH:mm')}
                        </p>
                      )}
                    </div>
                  </div>
                ) : (
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Send Response:</h4>
                    <div className="space-y-3">
                      <textarea
                        value={responses[message.id] || ''}
                        onChange={(e) => handleResponseChange(message.id, e.target.value)}
                        rows={4}
                        maxLength={500}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-600 resize-none"
                        placeholder="Type your response to help the user..."
                      />
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-500">
                          {(responses[message.id] || '').length}/500 characters
                        </span>
                        <button
                          onClick={() => handleSendResponse(message)}
                          disabled={responding === message.id || !(responses[message.id]?.trim())}
                          className="bg-primary-600 text-white px-4 py-2 rounded-md hover:bg-primary-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {responding === message.id ? (
                            <>
                              <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="h-4 w-4 mr-2" />
                              Send Response
                            </>
                          )}
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSupport;